import { Link } from 'react-router-dom';
import { useState, useEffect, useRef } from 'react';

interface NavigationProps {
  currentPage?: string;
}

export default function Navigation({ currentPage }: NavigationProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [curriculumDropdownOpen, setCurriculumDropdownOpen] = useState(false);
  const [aboutDropdownOpen, setAboutDropdownOpen] = useState(false);
  const [campusDropdownOpen, setCampusDropdownOpen] = useState(false);
  const [resultsDropdownOpen, setResultsDropdownOpen] = useState(false);
  const [newsDropdownOpen, setNewsDropdownOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const curriculumTimeout = useRef<NodeJS.Timeout | null>(null);
  const aboutTimeout = useRef<NodeJS.Timeout | null>(null);
  const campusTimeout = useRef<NodeJS.Timeout | null>(null);
  const resultsTimeout = useRef<NodeJS.Timeout | null>(null);
  const newsTimeout = useRef<NodeJS.Timeout | null>(null);

  // ------------------------------------------------------------
  // Scroll handling – adds a shadow / background when the page is scrolled
  // ------------------------------------------------------------
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // ------------------------------------------------------------
  // Curriculum dropdown handlers
  // ------------------------------------------------------------
  const handleCurriculumMouseEnter = () => {
    if (curriculumTimeout.current) {
      clearTimeout(curriculumTimeout.current);
      curriculumTimeout.current = null;
    }
    setCurriculumDropdownOpen(true);
  };

  const handleCurriculumMouseLeave = () => {
    curriculumTimeout.current = setTimeout(() => {
      setCurriculumDropdownOpen(false);
      curriculumTimeout.current = null;
    }, 500);
  };

  // ------------------------------------------------------------
  // About dropdown handlers
  // ------------------------------------------------------------
  const handleAboutMouseEnter = () => {
    if (aboutTimeout.current) {
      clearTimeout(aboutTimeout.current);
      aboutTimeout.current = null;
    }
    setAboutDropdownOpen(true);
  };

  const handleAboutMouseLeave = () => {
    aboutTimeout.current = setTimeout(() => {
      setAboutDropdownOpen(false);
      aboutTimeout.current = null;
    }, 500);
  };

  // ------------------------------------------------------------
  // Campus dropdown handlers
  // ------------------------------------------------------------
  const handleCampusMouseEnter = () => {
    if (campusTimeout.current) {
      clearTimeout(campusTimeout.current);
      campusTimeout.current = null;
    }
    setCampusDropdownOpen(true);
  };

  const handleCampusMouseLeave = () => {
    campusTimeout.current = setTimeout(() => {
      setCampusDropdownOpen(false);
      campusTimeout.current = null;
    }, 500);
  };

  // ------------------------------------------------------------
  // Results dropdown handlers
  // ------------------------------------------------------------
  const handleResultsMouseEnter = () => {
    if (resultsTimeout.current) {
      clearTimeout(resultsTimeout.current);
      resultsTimeout.current = null;
    }
    setResultsDropdownOpen(true);
  };

  const handleResultsMouseLeave = () => {
    resultsTimeout.current = setTimeout(() => {
      setResultsDropdownOpen(false);
      resultsTimeout.current = null;
    }, 500);
  };

  // ------------------------------------------------------------
  // News (학원소식) dropdown handlers
  // ------------------------------------------------------------
  const handleNewsMouseEnter = () => {
    if (newsTimeout.current) {
      clearTimeout(newsTimeout.current);
      newsTimeout.current = null;
    }
    setNewsDropdownOpen(true);
  };

  const handleNewsMouseLeave = () => {
    newsTimeout.current = setTimeout(() => {
      setNewsDropdownOpen(false);
      newsTimeout.current = null;
    }, 500);
  };

  // ------------------------------------------------------------
  // Render
  // ------------------------------------------------------------
  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-black/95 backdrop-blur-md shadow-lg shadow-black/20'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 cursor-pointer group">
            <div className="relative">
              <div className="absolute inset-0 bg-yellow-400/20 rounded-full blur-lg group-hover:bg-yellow-400/30 transition-colors"></div>
              <img
                src="https://public.readdy.ai/ai/img_res/4fe448fd-0b55-483a-8851-ff2b15a3c24d.png"
                alt="미래인재학원"
                className="w-12 h-12 object-contain relative z-10"
              />
            </div>
            <div>
              <div className="text-xl font-black text-white tracking-tight">미래인재학원</div>
              <p className="text-xs text-yellow-400 font-medium tracking-widest">ELITE OF FUTURE</p>
            </div>
          </Link>

          {/* Desktop navigation */}
          <div className="hidden md:flex items-center gap-1">
            {/* About Dropdown */}
            <div
              className="relative"
              onMouseEnter={handleAboutMouseEnter}
              onMouseLeave={handleAboutMouseLeave}
            >
              <button
                onClick={() => setAboutDropdownOpen(!aboutDropdownOpen)}
                className={`px-4 py-2 text-sm font-medium rounded-full transition-all flex items-center gap-1 ${
                  currentPage === 'about'
                    ? 'text-black bg-yellow-400'
                    : 'text-neutral-300 hover:text-white hover:bg-white/10'
                } whitespace-nowrap cursor-pointer`}
              >
                학원소개
                <i
                  className={`ri-arrow-down-s-line text-base transition-transform ${
                    aboutDropdownOpen ? 'rotate-180' : ''
                  }`}
                ></i>
              </button>

              {aboutDropdownOpen && (
                <div
                  className="absolute top-full left-0 mt-2 w-44 bg-neutral-900 border border-neutral-800 rounded-xl shadow-xl shadow-black/50 py-2 overflow-hidden"
                  onMouseEnter={handleAboutMouseEnter}
                  onMouseLeave={handleAboutMouseLeave}
                >
                  <Link
                    to="/about/history"
                    className="flex items-center gap-3 px-4 py-3 text-sm text-neutral-300 hover:text-yellow-400 hover:bg-yellow-400/10 transition-all cursor-pointer"
                  >
                    인사말/연혁
                  </Link>
                  <Link
                    to="/about/scholarship"
                    className="flex items-center gap-3 px-4 py-3 text-sm text-neutral-300 hover:text-yellow-400 hover:bg-yellow-400/10 transition-all cursor-pointer"
                  >
                    장학 제도
                  </Link>
                  <Link
                    to="/about/level-class"
                    className="flex items-center gap-3 px-4 py-3 text-sm text-neutral-300 hover:text-yellow-400 hover:bg-yellow-400/10 transition-all cursor-pointer"
                  >
                    레벨별 수업
                  </Link>
                  <Link
                    to="/about/small-class"
                    className="flex items-center gap-3 px-4 py-3 text-sm text-neutral-300 hover:text-yellow-400 hover:bg-yellow-400/10 transition-all cursor-pointer"
                  >
                    소규모 수업
                  </Link>
                  <Link
                    to="/about/management"
                    className="flex items-center gap-3 px-4 py-3 text-sm text-neutral-300 hover:text-yellow-400 hover:bg-yellow-400/10 transition-all cursor-pointer"
                  >
                    확실한 관리
                  </Link>
                </div>
              )}
            </div>

            {/* Curriculum Dropdown */}
            <div
              className="relative"
              onMouseEnter={handleCurriculumMouseEnter}
              onMouseLeave={handleCurriculumMouseLeave}
            >
              <button
                onClick={() => setCurriculumDropdownOpen(!curriculumDropdownOpen)}
                className={`px-4 py-2 text-sm font-medium rounded-full transition-all flex items-center gap-1 ${
                  currentPage === 'curriculum'
                    ? 'text-black bg-yellow-400'
                    : 'text-neutral-300 hover:text-white hover:bg-white/10'
                } whitespace-nowrap cursor-pointer`}
              >
                커리큘럼
                <i
                  className={`ri-arrow-down-s-line text-base transition-transform ${
                    curriculumDropdownOpen ? 'rotate-180' : ''
                  }`}
                ></i>
              </button>

              {curriculumDropdownOpen && (
                <div
                  className="absolute top-full left-0 mt-2 w-44 bg-neutral-900 border border-neutral-800 rounded-xl shadow-xl shadow-black/50 py-2 overflow-hidden"
                  onMouseEnter={handleCurriculumMouseEnter}
                  onMouseLeave={handleCurriculumMouseLeave}
                >
                  <Link
                    to="/curriculum/math"
                    className="flex items-center gap-3 px-4 py-3 text-sm text-neutral-300 hover:text-yellow-400 hover:bg-yellow-400/10 transition-all cursor-pointer"
                  >
                    수학
                  </Link>
                  <Link
                    to="/curriculum/english"
                    className="flex items-center gap-3 px-4 py-3 text-sm text-neutral-300 hover:text-yellow-400 hover:bg-yellow-400/10 transition-all cursor-pointer"
                  >
                    영어
                  </Link>
                  <Link
                    to="/curriculum/korean"
                    className="flex items-center gap-3 px-4 py-3 text-sm text-neutral-300 hover:text-yellow-400 hover:bg-yellow-400/10 transition-all cursor-pointer"
                  >
                    국어
                  </Link>
                  <Link
                    to="/curriculum/science"
                    className="flex items-center gap-3 px-4 py-3 text-sm text-neutral-300 hover:text-yellow-400 hover:bg-yellow-400/10 transition-all cursor-pointer"
                  >
                    과학
                  </Link>
                  <Link
                    to="/curriculum/winter-school"
                    className="flex items-center gap-3 px-4 py-3 text-sm text-neutral-300 hover:text-yellow-400 hover:bg-yellow-400/10 transition-all cursor-pointer"
                  >
                    윈터스쿨
                  </Link>
                </div>
              )}
            </div>

            <Link
              to="/teachers"
              className={`px-4 py-2 text-sm font-medium rounded-full transition-all ${
                currentPage === 'teachers'
                  ? 'text-black bg-yellow-400'
                  : 'text-neutral-300 hover:text-white hover:bg-white/10'
              } whitespace-nowrap cursor-pointer`}
            >
              강사진
            </Link>

            {/* Results Dropdown */}
            <div
              className="relative"
              onMouseEnter={handleResultsMouseEnter}
              onMouseLeave={handleResultsMouseLeave}
            >
              <button
                onClick={() => setResultsDropdownOpen(!resultsDropdownOpen)}
                className={`px-4 py-2 text-sm font-medium rounded-full transition-all flex items-center gap-1 ${
                  currentPage === 'results'
                    ? 'text-black bg-yellow-400'
                    : 'text-neutral-300 hover:text-white hover:bg-white/10'
                } whitespace-nowrap cursor-pointer`}
              >
                성적결과
                <i
                  className={`ri-arrow-down-s-line text-base transition-transform ${
                    resultsDropdownOpen ? 'rotate-180' : ''
                  }`}
                ></i>
              </button>

              {resultsDropdownOpen && (
                <div
                  className="absolute top-full left-0 mt-2 w-44 bg-neutral-900 border border-neutral-800 rounded-xl shadow-xl shadow-black/50 py-2 overflow-hidden"
                  onMouseEnter={handleResultsMouseEnter}
                  onMouseLeave={handleResultsMouseLeave}
                >
                  <Link
                    to="/results/admission"
                    className="flex items-center gap-3 px-4 py-3 text-sm text-neutral-300 hover:text-yellow-400 hover:bg-yellow-400/10 transition-all cursor-pointer"
                  >
                    대입 실적
                  </Link>
                  <Link
                    to="/results/school"
                    className="flex items-center gap-3 px-4 py-3 text-sm text-neutral-300 hover:text-yellow-400 hover:bg-yellow-400/10 transition-all cursor-pointer"
                  >
                    내신 실적
                  </Link>
                </div>
              )}
            </div>

            {/* News (학원소식) Dropdown */}
            <div
              className="relative"
              onMouseEnter={handleNewsMouseEnter}
              onMouseLeave={handleNewsMouseLeave}
            >
              <button
                onClick={() => setNewsDropdownOpen(!newsDropdownOpen)}
                className={`px-4 py-2 text-sm font-medium rounded-full transition-all flex items-center gap-1 ${
                  currentPage === 'notice' || currentPage === 'faq' || currentPage === 'reviews'
                    ? 'text-black bg-yellow-400'
                    : 'text-neutral-300 hover:text-white hover:bg-white/10'
                } whitespace-nowrap cursor-pointer`}
              >
                학원소식
                <i
                  className={`ri-arrow-down-s-line text-base transition-transform ${
                    newsDropdownOpen ? 'rotate-180' : ''
                  }`}
                ></i>
              </button>

              {newsDropdownOpen && (
                <div
                  className="absolute top-full left-0 mt-2 w-44 bg-neutral-900 border border-neutral-800 rounded-xl shadow-xl shadow-black/50 py-2 overflow-hidden"
                  onMouseEnter={handleNewsMouseEnter}
                  onMouseLeave={handleNewsMouseLeave}
                >
                  <Link
                    to="/notice"
                    className="flex items-center gap-3 px-4 py-3 text-sm text-neutral-300 hover:text-yellow-400 hover:bg-yellow-400/10 transition-all cursor-pointer"
                  >
                    공지사항
                  </Link>
                  <Link
                    to="/news/faq"
                    className="flex items-center gap-3 px-4 py-3 text-sm text-neutral-300 hover:text-yellow-400 hover:bg-yellow-400/10 transition-all cursor-pointer"
                  >
                    자주하는 질문
                  </Link>
                  <Link
                    to="/news/reviews"
                    className="flex items-center gap-3 px-4 py-3 text-sm text-neutral-300 hover:text-yellow-400 hover:bg-yellow-400/10 transition-all cursor-pointer"
                  >
                    학부모 후기
                  </Link>
                </div>
              )}
            </div>

            {/* Campus Dropdown */}
            <div
              className="relative"
              onMouseEnter={handleCampusMouseEnter}
              onMouseLeave={handleCampusMouseLeave}
            >
              <button
                onClick={() => setCampusDropdownOpen(!campusDropdownOpen)}
                className={`px-4 py-2 text-sm font-medium rounded-full transition-all flex items-center gap-1 ${
                  currentPage === 'campus'
                    ? 'text-black bg-yellow-400'
                    : 'text-neutral-300 hover:text-white hover:bg-white/10'
                } whitespace-nowrap cursor-pointer`}
              >
                캠퍼스안내
                <i
                  className={`ri-arrow-down-s-line text-base transition-transform ${
                    campusDropdownOpen ? 'rotate-180' : ''
                  }`}
                ></i>
              </button>

              {campusDropdownOpen && (
                <div
                  className="absolute top-full left-0 mt-2 w-44 bg-neutral-900 border border-neutral-800 rounded-xl shadow-xl shadow-black/50 py-2 overflow-hidden"
                  onMouseEnter={handleCampusMouseEnter}
                  onMouseLeave={handleCampusMouseLeave}
                >
                  <Link
                    to="/campus/jihang"
                    className="flex items-center gap-3 px-4 py-3 text-sm text-neutral-300 hover:text-yellow-400 hover:bg-yellow-400/10 transition-all cursor-pointer"
                  >
                    지행캠퍼스
                  </Link>
                  <Link
                    to="/campus/okjeong"
                    className="flex items-center gap-3 px-4 py-3 text-sm text-neutral-300 hover:text-yellow-400 hover:bg-yellow-400/10 transition-all cursor-pointer"
                  >
                    옥정캠퍼스
                  </Link>
                  <Link
                    to="/campus/millak"
                    className="flex items-center gap-3 px-4 py-3 text-sm text-neutral-300 hover:text-yellow-400 hover:bg-yellow-400/10 transition-all cursor-pointer"
                  >
                    민락캠퍼스
                  </Link>
                </div>
              )}
            </div>

            <Link
              to="/contact"
              className="ml-2 px-5 py-2.5 text-sm font-bold bg-yellow-400 text-black rounded-full hover:bg-yellow-300 transition-all shadow-lg shadow-yellow-400/25 whitespace-nowrap cursor-pointer"
            >
              입학상담
            </Link>

            {/* Social Media Icons - Desktop Only */}
            <div className="hidden md:flex items-center gap-2 ml-3">
              <a
                href="https://blog.naver.com/eliteoffuture"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="w-9 h-9 bg-[#03C75A] hover:scale-110 rounded-full flex items-center justify-center transition-all cursor-pointer shadow-md"
                title="네이버 블로그"
              >
                <span className="text-white font-bold text-xs">B</span>
              </a>
              <a
                href="https://www.instagram.com/eliteoffuture"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="w-9 h-9 bg-gradient-to-br from-[#833AB4] via-[#FD1D1D] to-[#F77737] hover:scale-110 rounded-full flex items-center justify-center transition-all cursor-pointer shadow-md"
                title="인스타그램"
              >
                <i className="ri-instagram-line text-white text-sm"></i>
              </a>
              <a
                href="https://www.youtube.com/@eliteoffuture"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="w-9 h-9 bg-[#FF0000] hover:scale-110 rounded-full flex items-center justify-center transition-all cursor-pointer shadow-md"
                title="유튜브"
              >
                <i className="ri-youtube-line text-white text-sm"></i>
              </a>
            </div>
          </div>

          {/* Mobile menu toggle */}
          <button
            className="md:hidden w-12 h-12 flex items-center justify-center bg-white/10 rounded-xl cursor-pointer hover:bg-white/20 transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <i className={`ri-${mobileMenuOpen ? 'close' : 'menu'}-line text-2xl text-white`}></i>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-neutral-900 border-t border-neutral-800">
          <div className="px-4 py-6 space-y-2">
            {/* Mobile About Submenu */}
            <div>
              <button
                onClick={() => setAboutDropdownOpen(!aboutDropdownOpen)}
                className={`w-full text-left px-4 py-3 text-sm font-medium rounded-xl flex items-center justify-between ${
                  currentPage === 'about'
                    ? 'text-black bg-yellow-400'
                    : 'text-neutral-300 hover:bg-white/10'
                } cursor-pointer`}
              >
                학원소개
                <i
                  className={`ri-arrow-down-s-line text-base transition-transform ${
                    aboutDropdownOpen ? 'rotate-180' : ''
                  }`}
                ></i>
              </button>
              {aboutDropdownOpen && (
                <div className="ml-4 mt-2 space-y-1 border-l-2 border-yellow-400/30 pl-4">
                  <Link
                    to="/about/history"
                    className="block py-2 text-sm text-neutral-400 hover:text-yellow-400 cursor-pointer"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    인사말/연혁
                  </Link>
                  <Link
                    to="/about/scholarship"
                    className="block py-2 text-sm text-neutral-400 hover:text-yellow-400 cursor-pointer"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    장학 제도
                  </Link>
                  <Link
                    to="/about/level-class"
                    className="block py-2 text-sm text-neutral-400 hover:text-yellow-400 cursor-pointer"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    레벨별 수업
                  </Link>
                  <Link
                    to="/about/small-class"
                    className="block py-2 text-sm text-neutral-400 hover:text-yellow-400 cursor-pointer"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    소규모 수업
                  </Link>
                  <Link
                    to="/about/management"
                    className="block py-2 text-sm text-neutral-400 hover:text-yellow-400 cursor-pointer"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    확실한 관리
                  </Link>
                </div>
              )}
            </div>

            {/* Mobile Curriculum Submenu */}
            <div>
              <button
                onClick={() => setCurriculumDropdownOpen(!curriculumDropdownOpen)}
                className={`w-full text-left px-4 py-3 text-sm font-medium rounded-xl flex items-center justify-between ${
                  currentPage === 'curriculum'
                    ? 'text-black bg-yellow-400'
                    : 'text-neutral-300 hover:bg-white/10'
                } cursor-pointer`}
              >
                커리큘럼
                <i
                  className={`ri-arrow-down-s-line text-base transition-transform ${
                    curriculumDropdownOpen ? 'rotate-180' : ''
                  }`}
                ></i>
              </button>
              {curriculumDropdownOpen && (
                <div className="ml-4 mt-2 space-y-1 border-l-2 border-yellow-400/30 pl-4">
                  <Link
                    to="/curriculum/math"
                    className="block py-2 text-sm text-neutral-400 hover:text-yellow-400 cursor-pointer"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    수학
                  </Link>
                  <Link
                    to="/curriculum/english"
                    className="block py-2 text-sm text-neutral-400 hover:text-yellow-400 cursor-pointer"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    영어
                  </Link>
                  <Link
                    to="/curriculum/korean"
                    className="block py-2 text-sm text-neutral-400 hover:text-yellow-400 cursor-pointer"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    국어
                  </Link>
                  <Link
                    to="/curriculum/science"
                    className="block py-2 text-sm text-neutral-400 hover:text-yellow-400 cursor-pointer"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    과학
                  </Link>
                  <Link
                    to="/curriculum/winter-school"
                    className="block py-2 text-sm text-neutral-400 hover:text-yellow-400 cursor-pointer"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    윈터스쿨
                  </Link>
                </div>
              )}
            </div>

            <Link
              to="/teachers"
              className={`block px-4 py-3 text-sm font-medium rounded-xl ${
                currentPage === 'teachers'
                  ? 'text-black bg-yellow-400'
                  : 'text-neutral-300 hover:bg-white/10'
              } cursor-pointer`}
              onClick={() => setMobileMenuOpen(false)}
            >
              강사진
            </Link>

            {/* Mobile Results Submenu */}
            <div>
              <button
                onClick={() => setResultsDropdownOpen(!resultsDropdownOpen)}
                className={`w-full text-left px-4 py-3 text-sm font-medium rounded-xl flex items-center justify-between ${
                  currentPage === 'results'
                    ? 'text-black bg-yellow-400'
                    : 'text-neutral-300 hover:bg-white/10'
                } cursor-pointer`}
              >
                성적결과
                <i
                  className={`ri-arrow-down-s-line text-base transition-transform ${
                    resultsDropdownOpen ? 'rotate-180' : ''
                  }`}
                ></i>
              </button>
              {resultsDropdownOpen && (
                <div className="ml-4 mt-2 space-y-1 border-l-2 border-yellow-400/30 pl-4">
                  <Link
                    to="/results/admission"
                    className="block py-2 text-sm text-neutral-400 hover:text-yellow-400 cursor-pointer"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    대입 실적
                  </Link>
                  <Link
                    to="/results/school"
                    className="block py-2 text-sm text-neutral-400 hover:text-yellow-400 cursor-pointer"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    내신 실적
                  </Link>
                </div>
              )}
            </div>

            {/* Mobile News (학원소식) Submenu */}
            <div>
              <button
                onClick={() => setNewsDropdownOpen(!newsDropdownOpen)}
                className={`w-full text-left px-4 py-3 text-sm font-medium rounded-xl flex items-center justify-between ${
                  currentPage === 'notice' || currentPage === 'faq' || currentPage === 'reviews'
                    ? 'text-black bg-yellow-400'
                    : 'text-neutral-300 hover:bg-white/10'
                } cursor-pointer`}
              >
                학원소식
                <i
                  className={`ri-arrow-down-s-line text-base transition-transform ${
                    newsDropdownOpen ? 'rotate-180' : ''
                  }`}
                ></i>
              </button>
              {newsDropdownOpen && (
                <div className="ml-4 mt-2 space-y-1 border-l-2 border-yellow-400/30 pl-4">
                  <Link
                    to="/notice"
                    className="block py-2 text-sm text-neutral-400 hover:text-yellow-400 cursor-pointer"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    공지사항
                  </Link>
                  <Link
                    to="/news/faq"
                    className="block py-2 text-sm text-neutral-400 hover:text-yellow-400 cursor-pointer"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    자주하는 질문
                  </Link>
                  <Link
                    to="/news/reviews"
                    className="block py-2 text-sm text-neutral-400 hover:text-yellow-400 cursor-pointer"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    학부모 후기
                  </Link>
                </div>
              )}
            </div>

            {/* Mobile Campus Submenu */}
            <div>
              <button
                onClick={() => setCampusDropdownOpen(!campusDropdownOpen)}
                className={`w-full text-left px-4 py-3 text-sm font-medium rounded-xl flex items-center justify-between ${
                  currentPage === 'campus'
                    ? 'text-black bg-yellow-400'
                    : 'text-neutral-300 hover:bg-white/10'
                } cursor-pointer`}
              >
                캠퍼스안내
                <i
                  className={`ri-arrow-down-s-line text-base transition-transform ${
                    campusDropdownOpen ? 'rotate-180' : ''
                  }`}
                ></i>
              </button>
              {campusDropdownOpen && (
                <div className="ml-4 mt-2 space-y-1 border-l-2 border-yellow-400/30 pl-4">
                  <Link
                    to="/campus/jihang"
                    className="block py-2 text-sm text-neutral-400 hover:text-yellow-400 cursor-pointer"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    지행캠퍼스
                  </Link>
                  <Link
                    to="/campus/okjeong"
                    className="block py-2 text-sm text-neutral-400 hover:text-yellow-400 cursor-pointer"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    옥정캠퍼스
                  </Link>
                  <Link
                    to="/campus/millak"
                    className="block py-2 text-sm text-neutral-400 hover:text-yellow-400 cursor-pointer"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    민락캠퍼스
                  </Link>
                </div>
              )}
            </div>

            <Link
              to="/contact"
              className="block px-4 py-3 text-sm font-bold text-center bg-yellow-400 text-black rounded-xl cursor-pointer mt-4"
              onClick={() => setMobileMenuOpen(false)}
            >
              입학상담
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}
